/**
 * Rate Limiting Service
 * 
 * Implements rate limiting with DynamoDB backend to protect against abuse
 * and denial-of-service attacks.
 */

import {
    RateLimitConfig,
    RateLimitEntry,
    RateLimitResult,
    SecurityEvent,
} from '../types/security.types';

/**
 * Rate limit configurations for different endpoints
 */
export const RATE_LIMIT_CONFIGS = {
  LOGIN: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    keyPrefix: 'login',
  },
  PASSWORD_RESET: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    keyPrefix: 'password_reset',
  },
  TOKEN_REFRESH: {
    maxAttempts: 10,
    windowMs: 60 * 1000, // 1 minute
    keyPrefix: 'token_refresh',
  },
} as const;

/**
 * In-memory storage for rate limiting (will be replaced with DynamoDB in production)
 */
class RateLimitStore {
  private store: Map<string, RateLimitEntry> = new Map();

  async get(key: string): Promise<RateLimitEntry | null> {
    const entry = this.store.get(key);
    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return null;
    }

    return entry;
  }

  async set(key: string, entry: RateLimitEntry): Promise<void> {
    this.store.set(key, entry);
  }

  async delete(key: string): Promise<void> {
    this.store.delete(key);
  }

  async cleanup(): Promise<void> {
    const now = Date.now();
    for (const [key, entry] of this.store.entries()) {
      if (now > entry.expiresAt) {
        this.store.delete(key);
      }
    }
  }
}

/**
 * Rate Limiting Service
 */
export class RateLimitService {
  private store: RateLimitStore;
  private securityEvents: SecurityEvent[] = [];

  constructor() {
    this.store = new RateLimitStore();
    
    // Cleanup expired entries every minute
    setInterval(() => {
      this.store.cleanup();
    }, 60 * 1000);
  }

  /**
   * Check if a request should be rate limited
   */
  async checkRateLimit(
    identifier: string,
    config: RateLimitConfig
  ): Promise<RateLimitResult> {
    const key = `${config.keyPrefix}:${identifier}`;
    const now = Date.now();
    const windowStart = now - config.windowMs;

    // Get existing entry
    const entry = await this.store.get(key);

    if (!entry) {
      // First request in window
      await this.store.set(key, {
        key,
        count: 1,
        expiresAt: now + config.windowMs,
        firstAttempt: now,
      });

      return {
        allowed: true,
        remaining: config.maxAttempts - 1,
        resetAt: now + config.windowMs,
      };
    }

    // Check if we're still in the same window
    if (entry.firstAttempt >= windowStart) {
      // Same window - increment count
      if (entry.count >= config.maxAttempts) {
        // Rate limit exceeded
        await this.logRateLimitEvent(identifier, config, entry);

        return {
          allowed: false,
          remaining: 0,
          resetAt: entry.expiresAt,
          retryAfter: Math.ceil((entry.expiresAt - now) / 1000),
        };
      }

      // Increment count
      entry.count++;
      await this.store.set(key, entry);

      return {
        allowed: true,
        remaining: config.maxAttempts - entry.count,
        resetAt: entry.expiresAt,
      };
    }

    // Window has expired - start new window
    await this.store.set(key, {
      key,
      count: 1,
      expiresAt: now + config.windowMs,
      firstAttempt: now,
    });

    return {
      allowed: true,
      remaining: config.maxAttempts - 1,
      resetAt: now + config.windowMs,
    };
  }

  /**
   * Check login rate limit (5 attempts per 15 minutes per IP)
   */
  async checkLoginRateLimit(ipAddress: string): Promise<RateLimitResult> {
    return this.checkRateLimit(ipAddress, RATE_LIMIT_CONFIGS.LOGIN);
  }

  /**
   * Check password reset rate limit (3 attempts per hour per email)
   */
  async checkPasswordResetRateLimit(email: string): Promise<RateLimitResult> {
    return this.checkRateLimit(email, RATE_LIMIT_CONFIGS.PASSWORD_RESET);
  }

  /**
   * Check token refresh rate limit (10 attempts per minute per user)
   */
  async checkTokenRefreshRateLimit(userId: string): Promise<RateLimitResult> {
    return this.checkRateLimit(userId, RATE_LIMIT_CONFIGS.TOKEN_REFRESH);
  }

  /**
   * Reset rate limit for a specific identifier
   */
  async resetRateLimit(identifier: string, config: RateLimitConfig): Promise<void> {
    const key = `${config.keyPrefix}:${identifier}`;
    await this.store.delete(key);
  }

  /**
   * Log rate limit exceeded event
   */
  private async logRateLimitEvent(
    identifier: string,
    config: RateLimitConfig,
    entry: RateLimitEntry
  ): Promise<void> {
    const event: SecurityEvent = {
      type: 'rate_limit',
      timestamp: new Date().toISOString(),
      ipAddress: identifier,
      details: {
        keyPrefix: config.keyPrefix,
        maxAttempts: config.maxAttempts,
        windowMs: config.windowMs,
        currentCount: entry.count,
        firstAttempt: new Date(entry.firstAttempt).toISOString(),
        expiresAt: new Date(entry.expiresAt).toISOString(),
      },
    };

    this.securityEvents.push(event);

    // In production, this would send to CloudWatch Logs or audit service
    console.warn('[SECURITY] Rate limit exceeded:', event);
  }

  /**
   * Get security events (for testing/monitoring)
   */
  getSecurityEvents(): SecurityEvent[] {
    return [...this.securityEvents];
  }

  /**
   * Clear security events (for testing)
   */
  clearSecurityEvents(): void {
    this.securityEvents = [];
  }
}

// Export singleton instance
export const rateLimitService = new RateLimitService();
